import React, { useEffect, useRef, useState } from "react";
import { View, TextInput, useColorScheme } from "react-native";
import { colors, radii, semanticColors, typography } from "@louise/moon-ui";

interface OTPInputProps {
  length?: number;
  onComplete?: (otp: string) => void;
  onChange?: (otp: string) => void;
  onInvalidInput?: () => void;
  className?: string;
  appearance?: "light" | "dark" | "glass" | "auto";
  /** Focus the first box when it appears. */
  autoFocus?: boolean;
  /** The light and glass boxes take the error border while the code is wrong. */
  hasError?: boolean;
}

export function OTPInput({
  length = 6,
  onComplete,
  onChange,
  onInvalidInput,
  className = "",
  appearance = "auto",
  autoFocus = false,
  hasError = false,
}: OTPInputProps) {
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(""));
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const inputRefs = useRef<TextInput[]>([]);
  const colorScheme = useColorScheme();
  const effectiveAppearance =
    appearance === "auto"
      ? colorScheme === "dark"
        ? "dark"
        : "light"
      : appearance;
  const isDark = effectiveAppearance === "dark";
  const isGlass = effectiveAppearance === "glass";
  const isLight = !isDark && !isGlass;

  useEffect(() => {
    if (autoFocus) inputRefs.current[0]?.focus();
  }, [autoFocus]);

  const handleChange = (value: string, index: number) => {
    // Keep only the digits before any length limit, so a pasted "123 456" or
    // "Your code is 123456" still reads as the code.
    const digits = value.replace(/\D/g, "");

    // Reject non-numeric input, but allow an empty value for backspace.
    if (value && !digits) {
      onInvalidInput?.();
      return;
    }

    // A digit typed into a box that already holds one replaces it.
    const previous = otp[index];
    const normalized =
      previous && digits.length === 2 && digits.startsWith(previous)
        ? digits.slice(1)
        : previous && digits.length === 2 && digits.endsWith(previous)
          ? digits.slice(0, 1)
          : digits;

    // A pasted or autofilled code: a whole code fills every box, a shorter
    // run fills on from this box, and the last box filled takes the focus.
    if (normalized.length > 1) {
      const start = normalized.length >= length ? 0 : index;
      const filled = normalized.slice(0, length - start).split("");
      const pastedOtp = [...otp];
      filled.forEach((digit, offset) => {
        pastedOtp[start + offset] = digit;
      });
      const lastFilled = start + filled.length - 1;
      const pastedString = pastedOtp.join("");

      setOtp(pastedOtp);
      setActiveIndex(lastFilled);
      inputRefs.current[lastFilled]?.focus();
      onChange?.(pastedString);
      if (pastedString.length === length) onComplete?.(pastedString);

      return;
    }

    const newOtp = [...otp];
    newOtp[index] = normalized;
    setOtp(newOtp);
    onChange?.(newOtp.join(""));

    const otpString = newOtp.join("");

    if (normalized && index < length - 1) {
      setActiveIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }

    if (otpString.length === length) {
      onComplete?.(otpString);
    }
  };

  const handleKeyPress = (key: string, index: number) => {
    // Backspace on an empty box goes back and clears the digit before it.
    if (key === "Backspace" && !otp[index] && index > 0) {
      const newOtp = [...otp];
      newOtp[index - 1] = "";
      setOtp(newOtp);
      onChange?.(newOtp.join(""));
      setActiveIndex(index - 1);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleFocus = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <View className={`flex flex-row justify-center gap-3 ${className}`}>
      {otp.map((digit, index) => (
        <TextInput
          key={index}
          id={`otp-${index}`}
          ref={(ref) => {
            if (ref) inputRefs.current[index] = ref;
          }}
          value={digit}
          onChangeText={(value) => handleChange(value, index)}
          onKeyPress={({ nativeEvent }) =>
            handleKeyPress(nativeEvent.key, index)
          }
          onFocus={() => handleFocus(index)}
          // The first box takes the code the phone or browser offers from a
          // message; the length limit is applied to the digits above, so no
          // maxLength cuts a pasted code short.
          autoComplete={index === 0 ? "one-time-code" : "off"}
          textContentType={index === 0 ? "oneTimeCode" : "none"}
          autoCorrect={false}
          keyboardType="number-pad"
          selectTextOnFocus
          testID={`otp-input-${index}`}
          style={[
            { width: `${Math.floor(100 / length)}%` },
            isGlass
              ? {
                  backgroundColor:
                    activeIndex === index
                      ? semanticColors.onDark.fill.subtle
                      : "transparent",
                  // The glass field's coral rim while the code is wrong.
                  borderColor: hasError
                    ? colors.coral
                    : activeIndex === index
                      ? "rgba(255, 239, 225, 0.28)"
                      : "rgba(255, 239, 225, 0.15)",
                  borderTopColor: hasError
                    ? colors.coral
                    : activeIndex === index
                      ? "rgba(255, 249, 244, 0.44)"
                      : "rgba(255, 244, 236, 0.28)",
                  borderBottomColor: hasError
                    ? colors.coral
                    : "rgba(255, 225, 207, 0.10)",
                  color: semanticColors.onDark.text.primary,
                }
              : isLight
                ? {
                    // The shared field's box: the same corners, fill and
                    // hairline, the label grey on the box being typed in.
                    borderRadius: radii.md,
                    backgroundColor: "rgba(255, 255, 255, 0.5)",
                    borderColor: hasError
                      ? semanticColors.status.danger
                      : activeIndex === index
                        ? semanticColors.border.focus
                        : semanticColors.border.subtle,
                    color: colors.text,
                    fontFamily: typography.families.sansMedium,
                  }
                : null,
          ]}
          className={`flex aspect-square max-w-14 items-center justify-center rounded-2xl border text-center text-2xl font-semibold outline-none ${
            isGlass || isLight
              ? ""
              : activeIndex === index
                ? isDark
                  ? "border-primary bg-primary/10 text-white"
                  : "border-primary bg-primary/10 text-foreground"
                : isDark
                  ? "border-white/20 bg-white/5 text-white"
                  : "border-border text-foreground bg-white"
          }`}
        />
      ))}
    </View>
  );
}

export default OTPInput;
